import React from 'react';
import { 
  DollarSign,
  CheckCircle,
  TrendingUp,
  FileText,
  AlertTriangle,
  CalendarDays
} from 'lucide-react';

const FeeStats = ({ stats, loading, campaignStats, campaignLoading, formatCurrency, onRefreshOverdue }) => {
  // Use new API fields if available, fall back to old ones or 0
  const fullyPaid = stats.fullyPaidCount !== undefined ? stats.fullyPaidCount : (stats.paid || 0);
  const partiallyPaid = stats.partiallyPaidCount !== undefined ? stats.partiallyPaidCount : (stats.partial || 0);
  const unpaid = stats.unpaidCount !== undefined ? stats.unpaidCount : (stats.pending || 0);
  const overdue = stats.overdueCount !== undefined ? stats.overdueCount : (stats.overdue || 0);
  const totalRecords = stats.totalRecords !== undefined ? stats.totalRecords : (stats.total || 0);
  const collectionRate = stats.collectionRate !== undefined ? stats.collectionRate : 
    (stats.totalAmount > 0 ? Math.round((stats.totalPaid / stats.totalAmount) * 100) : 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      {/* Card 1: Total Amount */}
      <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
        <div className="flex items-center">
          <div className="p-1.5 rounded" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
            <DollarSign className="h-4 w-4" style={{ color: 'var(--primary-color, #4f46e5)' }} />
          </div>
          <div className="ml-2.5">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Total Expected</p>
            <p className="text-base font-bold text-gray-900">{formatCurrency(stats.totalExpected || stats.totalAmount)}</p>
            <p className="text-[10px] text-gray-500">{loading ? 'Loading...' : `${totalRecords} total fee records`}</p>
          </div>
        </div>
      </div>
      
      {/* Card 2: Total Paid Amount */}
      <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
        <div className="flex items-center">
          <div className="p-1.5 bg-green-50 rounded">
            <CheckCircle className="h-4 w-4 text-green-600" />
          </div>
          <div className="ml-2.5">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Total Collected</p>
            <p className="text-base font-bold text-gray-900">{formatCurrency(stats.totalCollected || stats.totalPaid)}</p>
            <p className="text-[10px] text-gray-500">{loading ? 'Loading...' : `${fullyPaid} students fully paid`}</p>
          </div>
        </div>
      </div>
      
      {/* Card 3: Total Balance Due */}
      <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
        <div className="flex items-center">
          <div className="p-1.5 bg-red-50 rounded">
            <TrendingUp className="h-4 w-4 text-red-600" />
          </div>
          <div className="ml-2.5">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Total Balance Due</p>
            <p className="text-base font-bold text-gray-900">{formatCurrency(stats.totalPending || stats.balance)}</p>
            <p className="text-[10px] text-gray-500">{loading ? 'Loading...' : `${overdue} records overdue`}</p>
          </div>
        </div>
      </div>
      
      {/* Card 4: Collection Rate */}
      <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
        <div className="flex items-center">
          <div className="p-1.5 bg-purple-50 rounded">
            <FileText className="h-4 w-4 text-purple-600" />
          </div>
          <div className="ml-2.5">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Collection Rate</p>
            <p className="text-base font-bold text-gray-900">
              {loading ? '-' : `${collectionRate}%`}
            </p>
            <p className="text-[10px] text-gray-500">Target 100%</p>
          </div>
        </div>
      </div>
      
      {/* Student Status Breakdown (Spanning 2 columns) */}
      <div className="bg-white p-3 rounded shadow-sm border border-gray-200 md:col-span-2">
        <h3 className="text-xs font-bold text-gray-900 mb-2">Student Payment Status</h3>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-green-50 rounded border border-green-100">
            <p className="text-lg font-bold text-green-600">{fullyPaid}</p>
            <p className="text-[10px] font-medium text-green-800">Fully Paid</p>
          </div>
          <div className="text-center p-2 bg-yellow-50 rounded border border-yellow-100">
            <p className="text-lg font-bold text-yellow-600">{partiallyPaid}</p>
            <p className="text-[10px] font-medium text-yellow-800">Partially Paid</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded border border-red-100">
            <p className="text-lg font-bold text-red-600">{unpaid}</p>
            <p className="text-[10px] font-medium text-red-800">Unpaid</p>
          </div>
        </div>
      </div>

      {/* Overdue (Spanning 2 columns) */}
      <div className="bg-white p-3 rounded shadow-sm border border-gray-200 md:col-span-2 flex items-center justify-between">
         <div className="flex items-center">
          <div className="p-1.5 bg-red-50 rounded">
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </div>
          <div className="ml-2.5">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Overdue Records</p>
            <p className="text-base font-bold text-gray-900">{loading ? '-' : overdue}</p>
            <p className="text-[10px] text-gray-500">Requires immediate follow-up</p>
          </div>
        </div>
        <button 
            onClick={onRefreshOverdue} 
            className="px-2 py-1 border border-gray-300 rounded text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center space-x-1 transition-colors"
          >
            <CalendarDays className="h-3 w-3" />
            <span>Refresh</span>
          </button>
      </div>
      
      {/* Campaign Analytics Card */}
      <div className="bg-white p-4 rounded shadow-sm border border-gray-200 md:col-span-4 mt-3">
        <div className="flex items-center justify-between mb-4">
           <div>
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">Campaign Analytics (Last 30 Days)</h3>
              <p className="text-[10px] text-gray-500">WhatsApp Notification Performance</p>
           </div>
           {campaignLoading && <div className="text-[10px] text-gray-400 animate-pulse">Loading analytics...</div>}
        </div>
        
        {!campaignLoading && campaignStats ? (
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-blue-50 rounded border border-blue-100 flex flex-col items-center">
                 <span className="text-xs font-bold text-gray-500 uppercase">Sent</span>
                 <span className="text-2xl font-black text-blue-600">{campaignStats.sent || 0}</span>
              </div>
              <div className="p-3 bg-yellow-50 rounded border border-yellow-100 flex flex-col items-center">
                 <span className="text-xs font-bold text-gray-500 uppercase">Delivered</span>
                 <span className="text-2xl font-black text-yellow-600">{campaignStats.delivered || 0}</span>
              </div>
              <div className="p-3 bg-green-50 rounded border border-green-100 flex flex-col items-center">
                 <span className="text-xs font-bold text-gray-500 uppercase">Read</span>
                 <span className="text-2xl font-black text-green-600">{campaignStats.read || 0}</span>
              </div>
              <div className="p-3 bg-red-50 rounded border border-red-100 flex flex-col items-center">
                 <span className="text-xs font-bold text-gray-500 uppercase">Failed</span>
                 <span className="text-2xl font-black text-red-600">{campaignStats.failed || 0}</span>
              </div>
           </div>
        ) : (
           <div className="h-24 flex items-center justify-center bg-gray-50 rounded border border-gray-100 border-dashed">
              <p className="text-[11px] text-gray-400">{campaignLoading ? "Loading analytics..." : "No campaign data available"}</p>
           </div>
        )}
      </div>
    </div>
  );
};

export default FeeStats;
